import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import {
  SITE_ADVERTISING_CONFIG_ID,
  defaultSiteAdvertisingDocument,
  parseSiteAdvertisingConfig,
  type SiteAdvertisingDocument,
} from '../lib/siteAdvertisingConfig';

export interface SiteAdvertisingContextValue {
  config: SiteAdvertisingDocument;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SiteAdvertisingContext = createContext<SiteAdvertisingContextValue | null>(null);

export function SiteAdvertisingProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteAdvertisingDocument>(() => defaultSiteAdvertisingDocument());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('site_advertising_config')
        .select('config')
        .eq('id', SITE_ADVERTISING_CONFIG_ID)
        .maybeSingle();

      if (error) throw error;
      setConfig(parseSiteAdvertisingConfig(data?.config));
    } catch {
      setConfig(defaultSiteAdvertisingDocument());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const value = useMemo(
    () => ({
      config,
      loading,
      refresh,
    }),
    [config, loading, refresh]
  );

  return (
    <SiteAdvertisingContext.Provider value={value}>{children}</SiteAdvertisingContext.Provider>
  );
}

/** Colocated hook with provider (same pattern as CookieConsentContext). */
// eslint-disable-next-line react-refresh/only-export-components -- hook must live next to provider
export function useSiteAdvertisingConfig(): SiteAdvertisingContextValue {
  const ctx = useContext(SiteAdvertisingContext);
  if (!ctx) {
    throw new Error('useSiteAdvertisingConfig must be used within SiteAdvertisingProvider');
  }
  return ctx;
}
